import { useQuery } from "@tanstack/react-query";

import { axios } from "@/client/libs/axios";

/**
 * Lemon Squeezy checkout URLs, resolved server-side.
 *
 * [QA-K] The `VITE_LS_*_VARIANT_ID` env values are LS *internal* DB IDs and 404
 * on the public checkout endpoint — `/billing/config` returns the UUID slugs
 * that actually open a checkout. Anything building a checkout link must read
 * from here.
 *
 * Shared because four surfaces had each re-declared this exact type and query
 * (pricing page, onboarding wizard, onboarding ready-step, LinkedIn review
 * tool). Those still hold their own copies; new callers should use this one.
 */
export type BillingConfig = {
  proCheckoutUrl?: string;
  proYearlyCheckoutUrl?: string;
  /** Present only when the weekly LS checkout env is set. */
  proWeeklyCheckoutUrl?: string;
  ultimateCheckoutUrl?: string;
  ultimateYearlyCheckoutUrl?: string;
};

/** Same key the existing copies use, so they share one cache entry. */
export const BILLING_CONFIG_KEY = ["billing-config"];

export const useBillingConfig = () =>
  useQuery<BillingConfig | null>({
    queryKey: BILLING_CONFIG_KEY,
    queryFn: async () => {
      const { data } = await axios.get<BillingConfig | null>("/billing/config");
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
